import { UpperCasePipe } from '@angular/common'
import { HttpErrorResponse } from '@angular/common/http'
import {
	ChangeDetectionStrategy,
	Component,
	computed,
	input,
	InputSignal,
	OnDestroy,
	OnInit,
	output,
	OutputEmitterRef,
	Signal,
	signal,
	WritableSignal,
	inject,
} from '@angular/core'
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms'
import { OrderClassEnum } from '@modules/orders-module/enums/order-class.enum'
import { OrderSideEnum } from '@modules/orders-module/enums/order-side.enum'
import { OrderStatusEnum } from '@modules/orders-module/enums/order-status.enum'
import { OrderTypeEnum } from '@modules/orders-module/enums/order-type.enum'
import { SlTpValuesEnum } from '@modules/stocks-module/orders/enums/sl-tp-values.enum'
import { ChangeComplexOrderInterface } from '@modules/stocks-module/orders/interfaces/changed-complex-order.interface'
import { CreateOrderInterface } from '@modules/orders-module/interfaces/create-order.interface'
import { OrderInterface } from '@modules/orders-module/interfaces/order.interface'
import { TranslatePipe, TranslateService } from '@ngx-translate/core'
import { SlTpType } from '@shared/components/trading/orders/common/sl-tp-controls/core/types/sl-tp.type'
import { SlTpControlsComponent } from '@shared/components/trading/orders/common/sl-tp-controls/sl-tp-controls.component'
import { TuiIcon, TuiTextfield } from '@taiga-ui/core'
import { finalize, Subject, takeUntil } from 'rxjs'
import { SlTpInterface } from '@modules/stocks-module/orders/interfaces/sl-tp.interface'
import { SlTpEnum } from '@modules/stocks-module/orders/enums/sl-tp.enum'
import { HighlightDirective } from '@shared/directives/higlight.directive'
import { TitleOrderTypePipe } from '@modules/orders-module/pipes/title-order-type.pipe'
import { AlpacaAccountInterface } from '@modules/alpaca-module/alpaca-account/interfaces/alpaca-account.interface'

import { UseLocales } from '@modules/lang-module/decorators/use-locales'
import ru from './core/locale/ru.json'
import en from './core/locale/en.json'
import { PricePipe } from '@shared/pipes/price.pipe'
import { QuantityPipe } from '@shared/pipes/quantity.pipe'
import { AlertService } from '@shared/services/alert.service'
import { StocksOrdersService } from '@modules/stocks-module/orders/services/stocks-orders.service'
import { StocksOrdersHelperService } from '@modules/stocks-module/orders/services/stocks-orders-helper.service'
import { AlpacaAccountHelperService } from '@modules/alpaca-module/alpaca-account/services/alpaca-account-helper.service'
import { ButtonComponent } from '@shared/components/ui-kit/button/button.component'
import { OrderPriceInputComponent } from '@shared/components/trading/orders/common/order-price-input/order-price-input.component'

@UseLocales({ ru, en })
@Component({
	selector: 'app-change-complex-order',
	templateUrl: './change-complex-order.component.html',
	styleUrl: './change-complex-order.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [
		ReactiveFormsModule,
		TuiIcon,
		TranslatePipe,
		UpperCasePipe,
		SlTpControlsComponent,
		TuiTextfield,
		HighlightDirective,
		TitleOrderTypePipe,
		PricePipe,
		QuantityPipe,
		ButtonComponent,
		OrderPriceInputComponent,
	],
})
export class ChangeComplexOrderComponent implements OnInit, OnDestroy {
	protected readonly slTpEnum: typeof SlTpEnum = SlTpEnum
	protected readonly orderSideEnum: typeof OrderSideEnum = OrderSideEnum
	protected readonly slTpValuesEnum: typeof SlTpValuesEnum = SlTpValuesEnum
	protected readonly orderClassEnum: typeof OrderClassEnum = OrderClassEnum
	protected readonly orderStatusEnum: typeof OrderStatusEnum = OrderStatusEnum
	protected readonly orderTypeEnum: typeof OrderTypeEnum = OrderTypeEnum
	protected readonly number: NumberConstructor = Number

	public order: InputSignal<OrderInterface | null> = input<OrderInterface | null>(null)
	public closeDropdown: OutputEmitterRef<void> = output<void>()

	private _destroy$: Subject<void> = new Subject<void>()

	private readonly _formBuilder: FormBuilder = inject<FormBuilder>(FormBuilder)
	private readonly _ordersStocksService: StocksOrdersService = inject<StocksOrdersService>(StocksOrdersService)
	private readonly _alertService: AlertService = inject<AlertService>(AlertService)
	private readonly _translateService: TranslateService = inject<TranslateService>(TranslateService)
	private readonly _alpacaAccountHelperService: AlpacaAccountHelperService =
		inject<AlpacaAccountHelperService>(AlpacaAccountHelperService)
	private readonly _ordersStocksHelperService: StocksOrdersHelperService =
		inject<StocksOrdersHelperService>(StocksOrdersHelperService)

	protected slTpPrice: SlTpInterface<number> = {
		stopLoss: 0,
		takeProfit: 0,
	}

	protected alpacaAccount: WritableSignal<AlpacaAccountInterface | null> = signal<AlpacaAccountInterface | null>(null)
	protected targetPrice: WritableSignal<number> = signal<number>(0)
	protected approximateSum: WritableSignal<number> = signal<number>(0)
	protected isLoading: WritableSignal<boolean> = signal<boolean>(false)
	protected isSlTpActive: WritableSignal<SlTpInterface<boolean>> = signal({
		stopLoss: false,
		takeProfit: false,
	})
	protected form: FormGroup | null = null

	protected stopLoss: Signal<OrderInterface | null> = computed<OrderInterface | null>((): OrderInterface | null =>
		this._ordersStocksHelperService.findStopLoss(this.order()),
	)

	protected takeProfit: Signal<OrderInterface | null> = computed<OrderInterface | null>((): OrderInterface | null =>
		this._ordersStocksHelperService.findTakeProfit(this.order()),
	)

	protected isStopLossActive: Signal<boolean> = computed<boolean>((): boolean =>
		this._ordersStocksHelperService.isSlTpActive(this.stopLoss()),
	)

	protected isTakeProfitActive: Signal<boolean> = computed<boolean>((): boolean =>
		this._ordersStocksHelperService.isSlTpActive(this.takeProfit()),
	)

	protected isAveragePriceVisible: Signal<boolean> = computed<boolean>((): boolean => {
		const order: OrderInterface | null = this.order()

		return !!order && order.order_class === this.orderClassEnum.OCO
	})

	protected isStopPriceVisible: Signal<boolean> = computed<boolean>((): boolean => {
		const order: OrderInterface | null = this.order()

		return !!order && this._ordersStocksHelperService.checkIsStopPriceVisible(order.type, order.order_class)
	})

	protected isLimitPriceVisible: Signal<boolean> = computed<boolean>((): boolean => {
		const order: OrderInterface | null = this.order()

		return !!order && this._ordersStocksHelperService.checkIsLimitPriceVisible(order.type, order.order_class)
	})

	protected checkEditStopPrice: Signal<boolean> = computed<boolean>(
		(): boolean => this.isStopPriceVisible() && this.order()?.status === this.orderStatusEnum.NEW,
	)

	protected checkEditLimitPrice: Signal<boolean> = computed<boolean>(
		(): boolean => this.isLimitPriceVisible() && this.order()?.status === this.orderStatusEnum.NEW,
	)
	private _listenAlpacaAccount(): void {
		this._alpacaAccountHelperService.alpacaAccount$
			.pipe(takeUntil(this._destroy$))
			.subscribe((alpacaAccount: AlpacaAccountInterface | null): void => {
				this.alpacaAccount.set(alpacaAccount)
			})
	}

	private _initForm(): void {
		this.form = this._formBuilder.group({
			limit_price: [{ value: 0 }],
			stop_price: [{ value: 0 }],
		})
	}

	private _checkSlTp(): void {
		this.isSlTpActive.set({
			stopLoss: this.isStopLossActive(),
			takeProfit: this.isTakeProfitActive(),
		})

		this.updateTargetPrice()
	}

	private _fillForm(): void {
		this.form?.patchValue({
			limit_price: this.order()?.limit_price,
			stop_price: this.order()?.stop_price,
		})

		this._checkSlTp()
		this._updateApproximateSum()
	}

	private _listenFormControlsChanges(): void {
		this.form?.valueChanges.pipe(takeUntil(this._destroy$)).subscribe((): void => {
			this._updateApproximateSum()
		})
	}

	private _updateApproximateSum(): void {
		const order: OrderInterface | null = this.order()
		if (!order) return

		const formValue: CreateOrderInterface | undefined = this.form?.value
		const orderForCalculation: OrderInterface = {
			...order,
			limit_price: formValue?.limit_price ?? 0,
			stop_price: formValue?.stop_price ?? 0,
		}

		this.approximateSum.set(this._ordersStocksHelperService.getOrderPrice(orderForCalculation))
	}

	private _prepareComplexOrderToChange(): ChangeComplexOrderInterface | null {
		const formValue: CreateOrderInterface | undefined = this.form?.value
		const order: OrderInterface | null = this.order()
		if (!formValue || !order) return null

		const isOco: boolean = order?.order_class === this.orderClassEnum.OCO
		const isValidOtoAndBracket: boolean =
			[this.orderClassEnum.OTO, this.orderClassEnum.BRACKET].includes(order.order_class) &&
			order.status === this.orderStatusEnum.NEW

		return {
			id: order.id || '',
			...(isOco && {
				...(this.isSlTpActive().takeProfit && {
					limit_price: this.slTpPrice.takeProfit,
				}),
				stop_loss: { stop_price: this.slTpPrice.stopLoss },
			}),
			...(isValidOtoAndBracket && {
				...(this.isLimitPriceVisible() && {
					limit_price: formValue.limit_price,
				}),
				...(this.isStopPriceVisible() && {
					stop_price: formValue.stop_price,
				}),
			}),
			...(this.isSlTpActive().stopLoss && {
				stop_loss: { stop_price: this.slTpPrice.stopLoss },
			}),
			...(this.isSlTpActive().takeProfit &&
				!isOco && {
					take_profit: { limit_price: this.slTpPrice.takeProfit },
				}),
		}
	}

	private _getTargetPrice(): number {
		const order: OrderInterface | null = this.order()
		const formValue: CreateOrderInterface | undefined = this.form?.value
		if (!formValue || !order) return 0

		if (order.order_class === this.orderClassEnum.OCO) {
			return this.number(order?.avg_entry_price)
		}
		if (order.type === this.orderTypeEnum.MARKET) return order.price ?? 0
		if (order.type === this.orderTypeEnum.STOP) return formValue.stop_price ?? 0

		return formValue.limit_price ?? 0
	}

	protected onSubmit(): void {
		if (!this.order) return

		const complexOrder: ChangeComplexOrderInterface | null = this._prepareComplexOrderToChange()
		if (!complexOrder) return

		this.isLoading.set(true)

		this._ordersStocksService
			.changeComplexOrder(complexOrder)
			.pipe(
				takeUntil(this._destroy$),
				finalize((): void => this.isLoading.set(false)),
			)
			.subscribe(
				(): void => {
					this._alertService.success(this._translateService.instant('orders.orderSuccessfullyChanged'))
					this.onCloseDropdown()
				},
				(error: HttpErrorResponse): void => {
					this._alertService.error(
						error?.error?.error?.message || this._translateService.instant("orders.couldn'tChangeOrder"),
					)
				},
			)
	}

	protected togglePriceSlTp(type: SlTpType, value: number): void {
		this.slTpPrice = {
			...this.slTpPrice,
			[type === 'stop-loss' ? this.slTpEnum.STOP_LOSS : this.slTpEnum.TAKE_PROFIT]: value,
		}
	}

	protected updateTargetPrice(): void {
		this.targetPrice.set(this._getTargetPrice())
	}

	protected onCloseDropdown(): void {
		this.closeDropdown.emit()
	}

	ngOnInit(): void {
		this._initForm()
		this._listenFormControlsChanges()
		this._fillForm()
		this._listenAlpacaAccount()
	}

	ngOnDestroy(): void {
		this._destroy$.next()
		this._destroy$.complete()
	}
}
